import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowRight, Truck, ShieldCheck, Package, Plane, Ship, Train, Warehouse, FileCheck,
  Bike, Car, Wallet, MapPin, ScanLine, CheckCircle2,
  Brain, Scale, LineChart,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import DeliveryConfidenceBadge from "@/components/moveload/DeliveryConfidenceBadge";
import { HomeFAQ } from "@/components/moveload/HomeFAQ";
import { trackGrowthEvent } from "@/lib/growthEvents";
import { captureIntent, withIntent } from "@/lib/intent";
import ProductDemonstration from "@/components/landing/ProductDemonstration";
import { CommissionExplanation } from "@/components/moveload/CommissionExplanation";


const MODES = [
  { icon: Truck, title: "Road", status: "Live" },
  { icon: Plane, title: "Air", status: "On request" },
  { icon: Ship, title: "Sea", status: "Pilot" },
  { icon: Train, title: "Rail", status: "Pilot" },
  { icon: Warehouse, title: "Warehousing", status: "Soon" },
  { icon: FileCheck, title: "Customs", status: "Soon" },
];

const VEHICLES = [
  { icon: Bike, name: "Delivery bike", size: "up to 30 kg" },
  { icon: Car, name: "Van / bakkie", size: "500 kg – 1.5 t" },
  { icon: Truck, name: "1–4 ton", size: "urban distribution" },
  { icon: Truck, name: "8-ton tautliner", size: "regional loads" },
  { icon: Truck, name: "14-ton rigid", size: "line-haul" },
  { icon: Truck, name: "34-ton side tipper", size: "bulk & mining" },
];

const STEPS = [
  ["Tell us what moves", "Origin, destination, cargo and timing — or your vehicle, routes and availability."],
  ["We match and price", "Only verified operators enter the pool. MIPE prices it transparently, line by line."],
  ["Move, prove, get paid", "Barcode handovers, live tracking and signed POD release a controlled payout."],
];

export default function Landing() {
  const navigate = useNavigate();
  const [intent, setIntent] = useState<"ship" | "haul">("ship");
  const [quote, setQuote] = useState({ pickup: "", delivery: "", goods: "" });

  useEffect(() => {
    trackGrowthEvent("homepage_view", { path: "/" });
  }, []);

  // Every CTA persists the visitor's intent before leaving the page, so signup,
  // email confirmation and onboarding can all resume it.
  const goWithIntent = (partial: Parameters<typeof captureIntent>[0], source: string) => {
    const captured = captureIntent({ ...partial, source });
    navigate(withIntent("/auth/signup", captured));
  };

  const startQuote = (e: React.FormEvent) => {
    e.preventDefault();
    trackGrowthEvent("quote_started", { source: "hero" });
    goWithIntent({ role: "shipper", quote }, "hero");
  };

  const joinTransporter = () => {
    trackGrowthEvent("transporter_join_started", { source: "hero" });
    trackGrowthEvent("signup_started", { source: "hero", role: "transporter" });
    goWithIntent({ role: "transporter" }, "hero");
  };

  const shipperCta = (source: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    trackGrowthEvent("signup_started", { source, role: "shipper" });
    goWithIntent({ role: "shipper" }, source);
  };

  const transporterCta = (source: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    trackGrowthEvent("transporter_join_started", { source });
    trackGrowthEvent("signup_started", { source, role: "transporter" });
    goWithIntent({ role: "transporter" }, source);
  };

  return (
    <>
      <Seo
        title="Moveload — Freight quotes from the real route, verified transporters"
        description="Get freight quotes priced from the real route and match with verified South African transporters — from delivery bikes to 34-ton trucks. Register free, pay only when you book."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Do transporters have to search for loads?", acceptedAnswer: { "@type": "Answer", text: "No. Transporters set their vehicle, cargo, route and availability preferences once. Moveload matches suitable opportunities to them — they review and accept, not hunt." } },
            { "@type": "Question", name: "Does Moveload guarantee work or return loads?", acceptedAnswer: { "@type": "Answer", text: "No. Moveload matches transporters to suitable opportunities based on demand, compatibility and reliability. We do not guarantee continuous work or return loads." } },
            { "@type": "Question", name: "Is registration really free?", acceptedAnswer: { "@type": "Answer", text: "Yes. Registration, workspace and quotes are free. You only pay when you confirm a Moveload service." } },
            { "@type": "Question", name: "I only own one vehicle. Can I join as a transporter?", acceptedAnswer: { "@type": "Answer", text: "Yes. One vehicle is enough to start. Owner-drivers and single-vehicle operators are welcome." } },
          ],
        }}
      />

      {/* 1. EDITORIAL HERO — light canvas, blueprint grid, oversized display type */}
      <section className="relative overflow-hidden bg-canvas">
        <div aria-hidden className="absolute inset-0 bg-blueprint-fine pointer-events-none opacity-70 [mask-image:radial-gradient(120%_90%_at_70%_10%,black,transparent_75%)]" />
        <div aria-hidden className="absolute inset-y-0 right-0 w-full lg:w-2/3 bg-blueprint pointer-events-none [mask-image:linear-gradient(to_left,black,transparent)]" />
        <div aria-hidden className="absolute inset-0 bg-mesh pointer-events-none opacity-70" />

        <div className="container relative py-14 md:py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/5 border border-primary/15 px-3 py-1.5 text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.22em] text-cobalt">
                <span className="h-1.5 w-1.5 rounded-full bg-live animate-pulse" />
                South Africa's freight operating system
              </span>

              <h1 className="mt-6 font-display font-extrabold tracking-tighter text-[2.6rem] leading-[0.92] sm:text-6xl lg:text-[5.25rem] lg:leading-[0.88] text-foreground uppercase">
                Loads
                <br />
                <span className="text-gradient-live">moving</span>
                <br />
                smarter.
              </h1>

              <p className="mt-7 max-w-md text-base md:text-lg text-muted-foreground leading-relaxed">
                You tell us what needs to move. Moveload matches the right verified transport, prices it line by line,
                and proves every handover. Register free — pay only when you confirm a service.
              </p>

              <dl className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-6 border-t border-border pt-7">
                {[
                  ["40+", "Vehicle classes matched"],
                  ["100%", "Verified before pickup"],
                  ["R0", "To register or quote"],
                ].map(([k, v], i) => (
                  <div key={v} className="flex items-center gap-8">
                    {i > 0 && <span aria-hidden className="hidden sm:block h-10 w-px bg-border" />}
                    <div className="stat-hover">
                      <dt className="font-display text-3xl md:text-4xl font-extrabold text-cobalt tabular transition-colors duration-300 group-hover:text-primary">{k}</dt>
                      <dd className="mt-1 text-[10px] md:text-xs uppercase tracking-[0.16em] font-semibold text-muted-foreground">{v}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>

            {/* Dual-intent quote panel */}
            <div className="lg:col-span-5 w-full animate-fade-in-up">
              <div className="rounded-panel bg-card border border-border shadow-elevation-3 overflow-hidden hero-panel">
                <div role="tablist" aria-label="What do you want to do?" className="flex gap-1 p-1.5 bg-secondary/60 border-b border-border">
                  <button role="tab" aria-selected={intent === "ship"}
                    onClick={() => { setIntent("ship"); trackGrowthEvent("hero_intent_selected", { intent: "ship" }); }}
                    className={`flex-1 min-h-11 rounded-lg text-sm font-semibold transition press-sm ${intent === "ship" ? "bg-card text-foreground shadow-elevation-1" : "text-muted-foreground hover:text-foreground"}`}>
                    I need transport
                  </button>
                  <button role="tab" aria-selected={intent === "haul"}
                    onClick={() => { setIntent("haul"); trackGrowthEvent("hero_intent_selected", { intent: "haul" }); }}
                    className={`flex-1 min-h-11 rounded-lg text-sm font-semibold transition press-sm ${intent === "haul" ? "bg-card text-foreground shadow-elevation-1" : "text-muted-foreground hover:text-foreground"}`}>
                    I have a vehicle
                  </button>
                </div>

                <div className="p-5 sm:p-7">
                {intent === "ship" ? (
                  <form onSubmit={startQuote} className="space-y-5">
                    <h2 className="font-display text-2xl font-bold tracking-tight">Request a free quote</h2>
                    <div>
                      <Label htmlFor="pickup" className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Pickup point</Label>
                      <div className="relative mt-1.5">
                        <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input id="pickup" required autoComplete="address-level2" placeholder="Suburb or city" className="pl-9 pr-9 h-12 bg-secondary/60 border-transparent transition-colors duration-200 hover:bg-secondary focus-visible:border-live"
                          value={quote.pickup} onChange={e => setQuote(q => ({ ...q, pickup: e.target.value }))} />
                        <span aria-hidden className="route-dot absolute right-3.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-live text-live" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="delivery" className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Drop-off point</Label>
                      <div className="relative mt-1.5">
                        <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input id="delivery" required autoComplete="address-level2" placeholder="Suburb or city" className="pl-9 pr-9 h-12 bg-secondary/60 border-transparent transition-colors duration-200 hover:bg-secondary focus-visible:border-live"
                          value={quote.delivery} onChange={e => setQuote(q => ({ ...q, delivery: e.target.value }))} />
                        <span aria-hidden className="route-dot route-dot-delayed absolute right-3.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-accent text-accent" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="goods" className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">What are you moving?</Label>
                      <Input id="goods" required placeholder="e.g. 2 pallets of packaged food" className="h-12 mt-1.5 bg-secondary/60 border-transparent transition-colors duration-200 hover:bg-secondary focus-visible:border-live"
                        value={quote.goods} onChange={e => setQuote(q => ({ ...q, goods: e.target.value }))} />
                    </div>
                    <Button type="submit" size="lg" className="group w-full min-h-12 bg-accent text-accent-foreground hover:bg-accent/90 press">
                      Get a free quote <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">No card required. Quote is free and non-binding.</p>
                  </form>
                ) : (
                  <div className="space-y-5">
                    <h2 className="font-display text-2xl font-bold tracking-tight">Set your routes. We bring the work.</h2>
                    <ul className="space-y-3 text-sm">
                      {[
                        "Free workspace & document capture",
                        "Opportunities that fit your vehicle & corridor",
                        "Barcode handover, POD and controlled payout",
                      ].map(t => (
                        <li key={t} className="flex gap-3">
                          <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />{t}
                        </li>
                      ))}
                    </ul>
                    <Button size="lg" className="group w-full min-h-12 press" onClick={joinTransporter}>
                      Join the transport network <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      Matched opportunities activate once verification is approved. Moveload does not guarantee continuous work or return loads.
                    </p>
                  </div>
                )}
                </div>

                <p className="border-t border-border bg-secondary/40 px-5 py-3 text-center text-[11px] font-medium tracking-wide text-muted-foreground">
                  POD-gated payouts · Verified driver &amp; vehicle · Live tracking
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* 2. HOW IT WORKS — editorial numerals on white */}
      <section id="how-matching-works" className="bg-surface border-y border-border">
        <div className="container py-20 md:py-28">
          <div className="max-w-3xl">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-live">The workflow</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-[0.95]">
              Not a load board.
              <br />
              <span className="text-primary">A matching network.</span>
            </h2>
            <div aria-hidden className="mt-6 h-1 w-24 bg-primary" />
            <p className="mt-6 text-muted-foreground max-w-xl">
              Shippers describe demand. Transporters define capability, routes and availability.
              Moveload matches the right work to the right operator.
            </p>
          </div>

          <ol className="mt-16 grid md:grid-cols-3 gap-12 md:gap-16">
            {STEPS.map(([t, d], i) => (
              <li key={t} className="group">
                <div className="numeral-ghost text-7xl md:text-8xl transition-colors group-hover:text-live/20">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-6 font-display text-xl font-bold rule-grow">{t}</h3>
                <p className="mt-4 text-muted-foreground leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 2b. PRODUCT DEMONSTRATION — VX-3 */}
      <ProductDemonstration
        onStart={(source) => {
          trackGrowthEvent("signup_started", { source, role: "shipper" });
          goWithIntent({ role: "shipper", quote }, source);
        }}
      />



      {/* 3. TWO AUDIENCES — amber lane for shippers, blue lane for transporters */}
      <section className="container py-20 md:py-28 grid lg:grid-cols-2 gap-6 lg:gap-10">
        <div className="relative overflow-hidden rounded-panel bg-surface border border-border p-8 md:p-12">
          <div aria-hidden className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-accent/10" />
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-accent">For shippers</p>
          <h2 className="mt-4 font-display text-2xl md:text-3xl font-extrabold tracking-tight">
            Transport that helps the business grow.
          </h2>
          <ul className="mt-8 space-y-6">
            {[
              ["Transparent quotes from the real route", "Every line item visible before you commit."],
              ["Suitable vehicle recommendations", "Matched by weight, dimensions, corridor and readiness."],
              ["Verified assignments", "Driver, vehicle and documents checked before pickup."],
              ["Live tracking and signed POD", "Pickup and receiver codes protect both sides."],
            ].map(([t, d]) => (
              <li key={t} className="flex items-start gap-4">
                <span className="mt-1 h-6 w-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                  <span className="h-2 w-2 rounded-full bg-accent" />
                </span>
                <div>
                  <p className="font-semibold">{t}</p>
                  <p className="text-sm text-muted-foreground">{d}</p>
                </div>
              </li>
            ))}
          </ul>
          <Button asChild size="lg" className="mt-10 w-full sm:w-auto min-h-12 bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to="/auth/signup?role=shipper" onClick={shipperCta("segments")}>
              Get a free quote <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="relative overflow-hidden rounded-panel bg-hero text-white p-8 md:p-12">
          <div aria-hidden className="absolute inset-0 bg-blueprint-fine opacity-30 pointer-events-none" />
          <div aria-hidden className="absolute -bottom-24 -right-24 h-56 w-56 rounded-full bg-live/10" />
          <div className="relative">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-live">For transporters &amp; fleets</p>
            <h2 className="mt-4 font-display text-2xl md:text-3xl font-extrabold tracking-tight">
              Set your capability once. One vehicle is enough.
            </h2>
            <ul className="mt-8 space-y-6">
              {[
                ["Recommended opportunities", "Matched to your route, vehicle class and availability — you accept."],
                ["Documents & expiry tracking", "Vehicle, driver, insurance and PrDP with reminders."],
                ["A real TMS", "Fleet, dispatch, tracking, costs and utilisation — own-fleet loads carry no commission."],
                ["Controlled payouts", "Transparent statements released after POD."],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-4">
                  <span className="mt-1 h-6 w-6 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <span className="h-2 w-2 rounded-full bg-live" />
                  </span>
                  <div>
                    <p className="font-semibold">{t}</p>
                    <p className="text-sm text-white/65">{d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="min-h-12 bg-white text-primary hover:bg-white/90" onClick={joinTransporter}>
                Join the transport network
              </Button>
              <Button asChild size="lg" variant="outline" className="min-h-12 bg-white/5 border-white/25 text-white hover:bg-white/10">
                <Link to="/pricing">See commercial model</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 4. VEHICLE RANGE — wide editorial band */}
      <section className="bg-surface border-y border-border">
        <div className="container py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-[0.95] max-w-xl">
              Delivery bike to
              <br />
              34-ton side tipper
            </h2>
            <p className="max-w-sm text-muted-foreground">
              The right vehicle is matched to your load — no oversized trucks for a two-pallet job, no underspec
              vehicles for heavy cargo.
            </p>
          </div>

          <ul className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-y divide-border md:divide-y-0 md:divide-x">
            {VEHICLES.map(v => (
              <li key={v.name} className="group px-0 md:px-6 py-6 first:md:pl-0 last:md:pr-0">
                <v.icon className="h-6 w-6 text-primary transition-colors group-hover:text-live" />
                <p className="mt-5 font-display font-bold leading-snug">{v.name}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-muted-foreground">{v.size}</p>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-border pt-8">
            {MODES.map(m => (
              <span key={m.title} className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em]">
                <m.icon className="h-3.5 w-3.5 text-primary" />{m.title}
                <span className="font-medium tracking-normal normal-case text-muted-foreground">· {m.status}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 5. PROOF — the chain of custody, typeset not carded */}
      <section className="container py-20 md:py-28">
        <div className="max-w-3xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-live">Moveload AI</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-[0.95]">
            Proof, not promises.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-xl">
            Every quote, match and handover is scored by Moveload AI — with the reasons shown, not hidden.
          </p>

        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-10 md:gap-14">
          {[
            { icon: Scale, title: "Explainable pricing", desc: "Base freight, distance, tolls, cargo, vehicle, urgency and commission — every line item visible before you commit." },
            { icon: Brain, title: "Verified matching", desc: "Weight, dimensions, corridor, readiness and operator history are ranked next to each option." },
            { icon: ShieldCheck, title: "Tamper-evident custody", desc: "Geofenced pickup and delivery scans, timestamped events and signed POD travel with the load." },
          ].map(c => (
            <div key={c.title} className="group border-t-2 border-primary/15 pt-6 transition-colors hover:border-primary">
              <c.icon className="h-6 w-6 text-primary" />
              <h3 className="mt-5 font-display text-xl font-bold leading-snug">{c.title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        <ol className="mt-16 grid sm:grid-cols-5 gap-px bg-border rounded-panel overflow-hidden">
          {[
            { icon: ShieldCheck, t: "Verification" },
            { icon: Truck, t: "Verified assignment" },
            { icon: ScanLine, t: "Barcode handover" },
            { icon: LineChart, t: "Live tracking & POD" },
            { icon: Wallet, t: "Controlled payout" },
          ].map((x, i) => (
            <li key={x.t}>
              <button
                type="button"
                onClick={() => trackGrowthEvent("proof_card_click", { source: "trust_chain", card: x.t })}
                className="w-full h-full bg-surface p-5 text-left min-h-11 transition hover:bg-primary/5"
              >
                <span className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground tabular">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <x.icon className="mt-3 h-4 w-4 text-primary" />
                <p className="mt-3 text-xs font-semibold">{x.t}</p>
              </button>
            </li>
          ))}
        </ol>

        <div className="mt-10" onClick={() => trackGrowthEvent("proof_card_click", { source: "delivery_confidence" })}>
          <DeliveryConfidenceBadge />
        </div>
      </section>

      {/* 6. COMMERCIAL MODEL — one editorial paragraph */}
      <section className="bg-surface border-y border-border">
        <div className="container py-20 md:py-24 grid lg:grid-cols-12 gap-10">
          <h2 className="lg:col-span-5 font-display text-2xl md:text-3xl font-extrabold tracking-tight uppercase leading-[1.05]">
            A commercial model you can read in one paragraph.
          </h2>
          <div className="lg:col-span-7 space-y-4">
            <p className="text-lg text-muted-foreground leading-relaxed">Registration, quotes and your workspace are free.</p>
            <CommissionExplanation className="text-lg" />
          </div>
        </div>
      </section>

      <HomeFAQ />

      {/* 7. FINAL CTA */}
      <section className="bg-hero text-white relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 bg-blueprint-fine opacity-25 pointer-events-none" />
        <div className="container relative py-20 md:py-28 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tighter uppercase leading-[0.95] max-w-4xl mx-auto">
            Ready to move
            <br />
            <span className="text-live">with certainty?</span>
          </h2>
          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" className="min-h-12 px-10 bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/auth/signup?role=shipper" onClick={shipperCta("final_cta")}>
                <Package className="mr-2 h-4 w-4" />Get a free quote
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="min-h-12 px-10 bg-white/5 border-white/25 text-white hover:bg-white/10">
              <Link to="/auth/signup?role=transporter" onClick={transporterCta("final_cta")}>
                <Truck className="mr-2 h-4 w-4" />Join as a transporter
              </Link>
            </Button>
          </div>
          <p className="mt-8 text-sm text-white/60">
            Free registration. No card required. You only pay when you confirm a Moveload service.
          </p>
        </div>
      </section>
    </>
  );
}
